import { useMemo } from "react";
import { useApp } from "../context/AppState";
import { analyticsEventTypes } from "../helper";
import { useAnalytics } from "./useAnalytics";
import { useSubscribers } from "./useSubscribers";
import { usePopupFormContext } from "./usePopupFormContext";
import { useConfetti } from "./useConfetti";
import { POPUP_SECTIONS, TEMPLATE_TYPE } from "../constant/constant";
import cartService from "../services/cart.service";
import { getStepSectionsForPopup } from "../utils/popup-step-sections";
import { attachStorefrontTrackingToCartItems } from "../../shared/checkout-tracking.js";
import { resolveEffectiveOffer } from "../../shared/resolve-effective-offer.js";
import { isStaticCustomCartDrawer } from "../constants/custom-cart-drawer-template";
import { isStaticCartDrawerOpen } from "../utils/is-static-cart-drawer-open";
import { showCartAddError, showCartAddSuccess } from "../utils/cart-add-toast";
import {
  BUY_NOW_ACTION_BREEZE,
  BUY_NOW_ACTION_GOKWIK,
  BUY_NOW_ACTION_RAZORPAY,
  BUY_NOW_ACTION_SHIPROCKET,
} from "../../shared/product-upsell-recommendation-defaults.js";
import {
  tryClickThemeCheckoutButton,
  tryOpenBreezeCheckout,
  tryOpenGokwikCheckout,
  tryOpenShiprocketCheckout,
} from "../../shared/third-party-checkout.js";

function resolveOfferRuleIdAtAddTime({
  popupData,
  productData,
  productsArray,
  productIndex = 0,
  cartData,
}) {
  const fromProduct =
    productData?.__weupsellOfferRuleId ||
    productsArray?.[productIndex]?.__weupsellOfferRuleId;
  if (fromProduct != null && String(fromProduct) !== "") {
    return String(fromProduct);
  }

  const fromPopup = popupData?.__weupsellMatchedOfferRuleId;
  if (
    fromPopup != null &&
    String(fromPopup) !== "" &&
    String(fromPopup) !== "default"
  ) {
    return String(fromPopup);
  }

  const productCond = popupData?.generalSettings?.condition?.product;
  const cartItems = Array.isArray(cartData?.items) ? cartData.items : [];
  if (productCond && cartItems.length) {
    try {
      const contextProducts = cartItems.map((item) => ({
        id: item.product_id || item.productId || item.id,
        title: item.product_title || item.title,
        vendor: item.vendor,
      }));
      const effective = resolveEffectiveOffer(productCond, contextProducts);
      if (effective?.source === "rule" && effective.matchedRuleId) {
        return String(effective.matchedRuleId);
      }
      if (effective?.source === "fallback") {
        return "default";
      }
    } catch (_) {
      /* ignore resolve errors — fall through */
    }
  }

  if (fromPopup != null && String(fromPopup) !== "") {
    return String(fromPopup);
  }
  return "default";
}

const DEFAULT_DUPLICATE_MESSAGES = {
  text: "This value has already been used.",
  email: "This email has already been used.",
  number: "This number has already been used.",
  tel: "This phone number has already been used.",
  url: "This URL has already been used.",
  password: "This value has already been used.",
  textarea: "This message has already been used.",
  date: "This date has already been used.",
  time: "This time has already been used.",
  datetime: "This date and time has already been used.",
  search: "This search term has already been used.",
  color: "This value has already been used.",
  file: "This file has already been used.",
  range: "This value has already been used.",
  select: "This option has already been used.",
  checkbox: "This response has already been used.",
  radio: "This option has already been used.",
};

function resolveDuplicateMessage(section, inputType) {
  const configured = String(
    section?.rules?.duplicateMessage ??
      section?.rules?.duplicateEmailMessage ??
      ""
  ).trim();

  if (configured) return configured;

  return (
    DEFAULT_DUPLICATE_MESSAGES[inputType] ||
    DEFAULT_DUPLICATE_MESSAGES.text
  );
}

function isDuplicateSubscriberResponse(response) {
    return Boolean(
        response?.data?.error?.type === "duplicate" || response?.data?.isExist
    );
}

function getDuplicateSubscriberErrorType(response) {
    return response?.data?.error?.type || "duplicate";
}

function pickSubscriberInputSection(popupData, activeStep) {
    const inputSections = getStepSectionsForPopup(popupData, activeStep).filter(
        (section) => section?.type === POPUP_SECTIONS.INPUT
    );

    return (
        inputSections.find(
            (section) =>
                section?.inputType === "email" || section?.inputName === "email"
        ) || inputSections[0]
    );
}

function normalizeSubscriberValue(value, inputType) {
    const trimmed = String(value ?? "").trim();
    if (!trimmed) return "";
    if (inputType === "email") return trimmed.toLowerCase();
    return trimmed;
}

export const useFormSumbmit = ({ popupData, setPopupState, productIndex, productData, handleClose, section ,activeStep}) => {
    const { appState, toast,setCartOpen, cartData, setCartData } = useApp();
    const { watch, setError, clearErrors } = usePopupFormContext();
    const popupValue = watch();
    const { addAnalyticsEvent } = useAnalytics();
    const popupType = useMemo(() => popupData?.popUpType, [popupData]);
    const popupId = useMemo(() => popupData?._id, [popupData]);
    const confetti = useConfetti(popupData);
   
    const productsArray = getStepSectionsForPopup(popupData, activeStep)
      .filter((section) => section?.type === POPUP_SECTIONS.PRODUCT)
      .flatMap((section) => section?.products || []);

    const resolvedProductIndex =
      typeof productIndex === "number" && !Number.isNaN(productIndex)
        ? productIndex
        : 0;

    const buildCartItems = (selling_plan, cartDrawer = false, cartDrawerUpsell = false) => {

    const isCustomCartCheckout = popupData?.popUpType === TEMPLATE_TYPE?.CUSTOM_CART && cartDrawer;
    const itemsLength = isCustomCartCheckout ? cartData?.items?.length || 0 : productsArray?.length || 0;
    const isBundleProduct = popupData?.popUpType === TEMPLATE_TYPE?.BOGO_BUNDLE;
    const shouldAttachTracking = !isCustomCartCheckout;
    const resolvedOfferRuleId = resolveOfferRuleIdAtAddTime({
      popupData,
      productData,
      productsArray,
      productIndex: resolvedProductIndex,
      cartData,
    });

    const trackingCtx = {
      popup: popupData,
      shop: window?.Shopify?.shop || window.location.hostname,
      product: productData,
      productIndex: resolvedProductIndex,
      getProductForIndex: (index) => productsArray?.[index] || productData,
      cartDrawerUpsell: cartDrawerUpsell === true || Boolean(cartDrawerUpsell),
      offerRuleId: resolvedOfferRuleId,
    };

    const attachIfNeeded = (items) =>
      shouldAttachTracking
        ? attachStorefrontTrackingToCartItems(items, trackingCtx)
        : items;

    // 🟡 CASE 1 multiple products
    if (isBundleProduct || isCustomCartCheckout) {
      const items = Array.from({ length: itemsLength }, (_, index) => {
        let variantId;
        let quantity;

        if (isCustomCartCheckout) {
          const cartItem = cartData?.items?.[index];
          variantId = cartItem?.variant_id ?? cartItem?.id;
          quantity = cartItem?.quantity ?? 1;
        } else {
          variantId =
            popupValue?.[`${POPUP_SECTIONS.VARIANT_PICKER}-${index}`] ??
            productData?.variants?.[0]?.id ??
            productData?.variants?.[0]?.admin_graphql_api_id?.split("/")?.pop();
          quantity =
            popupValue?.[`${POPUP_SECTIONS.QUANTITY_PICKER}-${index}`] ?? 1;
        }

        const sellingPlanGroupValue =
          popupValue?.[`${POPUP_SECTIONS.SUBSCRIPTION_PICKER}-${index}`];
        const sellingPlanRadioValue =
          popupValue?.[`${POPUP_SECTIONS.SUBSCRIPTION}-${index}`];

        const sellingPlanValue =
          sellingPlanGroupValue != null
            ? Number(sellingPlanGroupValue)
            : sellingPlanRadioValue != null
            ? Number(sellingPlanRadioValue)
            : selling_plan
            ? Number(selling_plan)
            : null;

        return {
          id: variantId,
          quantity,
          ...(sellingPlanValue ? { selling_plan: sellingPlanValue } : {}),
        };
      });

      return attachIfNeeded(items);
    }

    // 🟢 CASE 2: Single Product (not a bundle)
    const singleVariantId =
      popupValue?.[`${POPUP_SECTIONS.VARIANT_PICKER}-${resolvedProductIndex}`] ??
      productData?.variants?.[0]?.id ??
      productData?.variants?.[0]?.admin_graphql_api_id?.split("/")?.pop();

    const singleQuantity =
      popupValue?.[`${POPUP_SECTIONS.QUANTITY_PICKER}-${resolvedProductIndex}`] ??
      1;

    const singleSellingPlanGroup =
      popupValue?.[`${POPUP_SECTIONS.SUBSCRIPTION_PICKER}-${resolvedProductIndex}`];
    const singleSellingPlanRadio =
      popupValue?.[`${POPUP_SECTIONS.SUBSCRIPTION}-${resolvedProductIndex}`];

    const singleSellingPlanValue =
      singleSellingPlanGroup != null
        ? Number(singleSellingPlanGroup)
        : singleSellingPlanRadio != null
        ? Number(singleSellingPlanRadio)
        : selling_plan
        ? Number(selling_plan)
        : null;

    const item = {
      id: singleVariantId,
      quantity: singleQuantity,
      ...(singleSellingPlanValue
        ? { selling_plan: singleSellingPlanValue }
        : {}),
    };

    return attachIfNeeded([item]);
  };

    const { createSubscribers } = useSubscribers(popupData);

    const advancePopupStep = (step) => {
        confetti.fire();

        setPopupState((prev) => ({
            ...prev,
            [`${popupType}_${popupId}`]: {
                ...prev[`${popupType}_${popupId}`],
                activeStep:
                    step > -1
                        ? step
                        : prev[`${popupType}_${popupId}`]?.activeStep + 1,
            },
        }));

        return { ok: true };
    };

    const getCouponCode = async (step) => {
        const subscriberSection = pickSubscriberInputSection(
            popupData,
            activeStep
        );

        if (!subscriberSection) {
            return advancePopupStep(step);
        }

        const subscriberFieldName =
            subscriberSection?.inputName ?? "email";
        const subscriberInputType = subscriberSection?.inputType || "text";
        const subscriberValue = normalizeSubscriberValue(
            popupValue?.[subscriberFieldName],
            subscriberInputType
        );
        const duplicateMsg = resolveDuplicateMessage(
            subscriberSection,
            subscriberInputType
        );
        const requiredMsg =
            String(subscriberSection?.rules?.requiredMessage ?? "").trim() ||
            "Please complete this field";

        clearErrors(subscriberFieldName);

        if (!subscriberValue) {
            setError(subscriberFieldName, {
                type: "required",
                message: requiredMsg,
            });
            return { ok: false };
        }

        const response = await createSubscribers(
            subscriberValue,
            appState?.shop?.shop,
            popupValue,
            {
                inputType: subscriberInputType,
                fieldName: subscriberFieldName,
            }
        );

        if (!response) {
            toast.error(
                "Unable to submit. Check your connection and try again."
            );
            return { ok: false };
        }

        if (isDuplicateSubscriberResponse(response)) {
            const duplicateMessage = duplicateMsg;
            setError(subscriberFieldName, {
                type: getDuplicateSubscriberErrorType(response),
                message: duplicateMessage,
            });
            toast?.error?.(duplicateMessage);
            return { ok: false, duplicate: true };
        }

        if (response?.success) {
            return advancePopupStep(step);
        }

        toast.error(
            response?.message || "Something went wrong. Please try again."
        );
        return { ok: false };
    };

    const upgradeOrder = async (formData) => {
       
        const { selling_plan : sellingPlanField, purchase_option } = formData;

        let selling_plan = null;

        if ('purchase_option' in formData) {
            if(purchase_option === "true"){
                selling_plan = sellingPlanField;
            }
        }else{
            selling_plan = sellingPlanField;
        }


        try { 

            const items = buildCartItems(selling_plan);

            const result = await cartService.addToCart(items, {
              attachTracking: false,
              suppressModalOnCartAddRefire: true,
            });

            if (result) {
                // await addAnalyticsEvent({
                //     eventType: analyticsEventTypes.INTERACTION,
                //     popupType: popupData?.popUpType,
                //     popupId: popupData?._id,
                //     ...(popupData?.campaign_Id && { campaignId: popupData?.campaign_Id }),
                // });
            }

        } catch (error) {
            console.error(error);
            showCartAddError(error);
        } finally {
            handleClose(false);
            setTimeout(() => {
                window.location.href = "/cart";
            }, 100);
        }

    };

    const addCart = async (formData, refreshWindow, openCustomCartDrawer=false, isCustomCart, preventClose=false, cartDrawerUpsell=false) => {

        const { selling_plan : sellingPlanField, purchase_option } = formData || {};

        let selling_plan = null;

        if ('purchase_option' in formData) {
            if(purchase_option === "true"){
                selling_plan = sellingPlanField;
            }
        } else{
            selling_plan = sellingPlanField;
        }

        let success = false;
        try {
            const discountDetails =
            productsArray?.find((p) => p?.id === productData?.id)?.discount_details || null;

            const items = buildCartItems(selling_plan, false, cartDrawerUpsell);
            const validItems = (items || []).filter(
              (item) => item?.id != null && item.id !== ""
            );

            if (!validItems.length) {
              toast?.error?.(
                "Unable to add to cart. Please select a product variant."
              );
              return false;
            }

            const result = await cartService.addToCart(validItems, {
              attachTracking: false,
              suppressModalOnCartAddRefire: true,
            });

            if (result) {
                try {
                    if (discountDetails?.discount_code) {
                        await cartService.updateCart({
                            discount: discountDetails.discount_code,
                            discount_details: discountDetails,
                        });
                    }
                } catch (_) {}
            }

            let cartSnapshot = null;
            if (result) {
                cartSnapshot = await cartService.fetchCart();
                setCartData(cartSnapshot);
            }

            if (result) {
                if(openCustomCartDrawer){
                    setCartOpen(true);
                }
                if (typeof window !== "undefined") {
                  const drawerAlreadyOpen = isStaticCartDrawerOpen();
                  const customDrawerTakeover =
                    window.__WEUPSELL_STATIC_CART_DRAWER_ENABLED__ === true;
                  // Product page Horizontal/Carousel/+ Add: open custom cart drawer
                  // when takeover is enabled (hybrid may also open via fetch patch).
                  if (
                    customDrawerTakeover &&
                    !drawerAlreadyOpen &&
                    typeof window.weupsellOpenStaticCartDrawer === "function"
                  ) {
                    window.weupsellOpenStaticCartDrawer({
                      source: "product_page_upsell_add_cart",
                    });
                  } else if (isStaticCustomCartDrawer(popupData)) {
                    const isThemeCartUpsellInject =
                      popupData?.generalSettings?.condition?.product
                        ?.allowCustomerCartDrawer === false;

                    if (drawerAlreadyOpen) {
                      window.dispatchEvent(
                        new CustomEvent("weupsell:static-cart-drawer-cart-updated", {
                          detail: cartSnapshot ? { cart: cartSnapshot } : undefined,
                        })
                      );
                      if (typeof window.weupsellRefreshCartDrawerOffers === "function") {
                        window.weupsellRefreshCartDrawerOffers(
                          "upsell_add_while_drawer_open"
                        );
                      }
                    } else if (isThemeCartUpsellInject) {
                      // Theme default cart + injected upsell host: re-resolve offers
                      // so cards update after + Add (exclude newly added lines).
                      if (cartSnapshot && typeof cartSnapshot === "object") {
                        window.__weupsellCartJsFull = cartSnapshot;
                        window.__weupsellCartJsCacheGen =
                          Number(window.__weupsellCartJsGeneration) || 0;
                        window.__weupsellCart = {
                          total: cartSnapshot.total_price || 0,
                          itemCount: cartSnapshot.item_count || 0,
                          items: Array.isArray(cartSnapshot.items)
                            ? cartSnapshot.items
                            : [],
                        };
                      }
                      if (typeof window.weupsellRefreshCartDrawerOffers === "function") {
                        window.weupsellRefreshCartDrawerOffers(
                          "theme_cart_upsell_add"
                        );
                      }
                    } else {
                      window.dispatchEvent(
                        new CustomEvent("weupsell:static-cart-drawer-refresh")
                      );
                    }
                  }
                }
                success = true;
                showCartAddSuccess();
            }

                if (result?.sections) {
                    Object.entries(result.sections).forEach(([sectionId, html]) => {
                        const el = document.getElementById(sectionId);
                        if (el) el.innerHTML = html;
                    });
                }

        } catch (error) {
            console.error('[useFormSubmit] Cart operation failed:', error);
            showCartAddError(error);
        } finally {
            if (refreshWindow) {
                window.location.reload();
            } else if (!isCustomCart && !preventClose) {
                handleClose();
            }
        }
        return success;
    };

    const checkout = async (formData, cartDrawer, options = {}) => {
        const buyNowAction = options?.buyNowAction;
        const clickEvent = options?.clickEvent;

        if (buyNowAction === BUY_NOW_ACTION_SHIPROCKET) {
            const added = await addCart(formData, false, false, false, true);
            if (!added) return;
            if (tryOpenShiprocketCheckout(clickEvent)) return;
            window.location.href = "/checkout";
            return;
        }

        if (buyNowAction === BUY_NOW_ACTION_GOKWIK) {
            const added = await addCart(formData, false, false, false, true);
            if (!added) return;
            if (tryOpenGokwikCheckout(clickEvent, { preferBuyNow: true })) return;
            window.location.href = "/checkout";
            return;
        }

        if (buyNowAction === BUY_NOW_ACTION_BREEZE) {
            const added = await addCart(formData, false, false, false, true);
            if (!added) return;
            if (tryOpenBreezeCheckout()) return;
            window.location.href = "/checkout";
            return;
        }

        if (buyNowAction === BUY_NOW_ACTION_RAZORPAY) {
            const added = await addCart(formData, false, false, false, true);
            if (!added) return;
            if (tryClickThemeCheckoutButton()) return;
            window.location.href = "/checkout";
            return;
        }

        const { selling_plan : sellingPlanField, purchase_option } = formData;

        let selling_plan = null;

        if ('purchase_option' in formData) {
            if(purchase_option === "true"){
                selling_plan = sellingPlanField;
            }
        }else{
            selling_plan = sellingPlanField;
        }

        try {

            const items = buildCartItems(selling_plan,cartDrawer);

            const result = await cartService.getCheckoutUrl(items);

            if (result) {
                // await addAnalyticsEvent({
                //     eventType: analyticsEventTypes.INTERACTION,
                //     popupType: popupData?.popUpType,
                //     popupId: popupData?._id,
                //     ...(popupData?.campaign_Id && { campaignId: popupData?.campaign_Id }),
                // });

                window.location.href = result?.data?.data?.cart?.checkoutUrl;
            }
        } catch (error) {
            console.error(error);
        }
    };



    return {
        upgradeOrder,
        getCouponCode,
        addCart,
        checkout
    };
};